/**
 * Retry a Supabase edge-function call through cold-start CORS/preflight failures.
 *
 * A low-traffic `verify_jwt` edge function, when cold, can fail the FIRST
 * request's CORS preflight while it boots — the browser reports
 * `net::ERR_FAILED` / "Failed to send a request to the Edge Function" with no
 * `Access-Control-Allow-Origin` header, even though the function's OPTIONS
 * handler is correct. The first attempt still triggers the boot, so a
 * short-delayed retry hits the now-warm function and succeeds. This smooths the
 * visa-check first-click error and the OpenRouter observability dashboard's
 * intermittent "degraded" pill (both low-traffic functions that go cold between
 * uses).
 *
 * Only transient fetch/network failures are retried. A real HTTP error the
 * function actually returned (a 4xx/5xx with a body) is passed through
 * unchanged — retrying those would just repeat the same failure.
 *
 * IDEMPOTENCY CONTRACT — wrap only operations that are SAFE TO REPLAY. The
 * browser cannot distinguish a failed CORS PREFLIGHT (the request never reached
 * the function — the common cold-start case, where a replay costs nothing) from
 * a lost RESPONSE to a request that already executed, so a retry may re-invoke
 * the function. The two current callers are safe: the visa check is READ-ONLY
 * (a replay returns the same requirements and writes nothing — worst case a rare
 * duplicated, same-result AI call, never a data change) and the observability
 * read is a GET. Do NOT wrap a non-idempotent mutation with this without a
 * client request id + server-side dedup, or a replay could apply the side
 * effect twice.
 */

const COLD_START_RETRY_DELAY_MS = 800;
const COLD_START_MAX_RETRIES = 2;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * True when an error looks like a browser-side fetch/network failure (including
 * a CORS-preflight failure on a cold function), as opposed to an HTTP error the
 * function actually returned. Covers supabase-js `FunctionsFetchError` and raw
 * `fetch` rejections.
 */
export function isColdStartFetchError(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: unknown })?.name;
  const message = String((err as { message?: unknown })?.message ?? err);
  return (
    name === 'FunctionsFetchError' ||
    /failed to fetch|failed to send a request|networkerror|load failed|err_failed|fetch failed/i.test(
      message,
    )
  );
}

/**
 * Runs `attempt` and retries it on a cold-start fetch failure. Works for both
 * shapes of Supabase call:
 *  - `supabase.functions.invoke` resolves to `{ data, error }`; pass `errorOf`
 *    to surface `result.error` so a `FunctionsFetchError` triggers a retry.
 *  - a raw `fetch(...)` rejects on network failure; the thrown error is retried.
 *
 * The delay grows per attempt (800ms, then 1600ms) to give a slow cold boot time
 * to finish.
 */
export async function withColdStartRetry<T>(
  attempt: () => Promise<T>,
  errorOf: (result: T) => unknown = () => null,
): Promise<T> {
  for (let tries = 0; ; tries += 1) {
    try {
      const result = await attempt();
      const surfaced = errorOf(result);
      if (surfaced && isColdStartFetchError(surfaced) && tries < COLD_START_MAX_RETRIES) {
        await sleep(COLD_START_RETRY_DELAY_MS * (tries + 1));
        continue;
      }
      return result;
    } catch (err) {
      if (isColdStartFetchError(err) && tries < COLD_START_MAX_RETRIES) {
        await sleep(COLD_START_RETRY_DELAY_MS * (tries + 1));
        continue;
      }
      throw err;
    }
  }
}
